import { useState, useMemo, useEffect } from 'react'
import * as store from '@store'
import * as actions from '@actions'
import { toast } from 'react-toastify'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import useGlobalStateQuery from '@hook/useGlobalStateQuery'
import cn from 'clsx'
import { TAB_TYPE_DEED } from '@/constants'
import { MdArticle, MdAutoAwesomeMosaic } from 'react-icons/md'

const TAB_TYPE_MAP = {
    deed: 'Deed',
    lease: 'Leasehold',
}

export const SelectRunsheetDialog = ({
    confirmButtonText = 'Open',
    open,
    setOpen,
    showTabs = false,
    showAssign = false,
    setCreateRunsheetDialogOpen,
    onSelectRunsheet,
    initialSelectedRunsheet,
}) => {
    
    const [query, setQuery] = useState('')
    const [selectedRunsheet, setSelectedRunsheet] = useState(initialSelectedRunsheet)
    const [ownershipType, setOwnershipType] = useState(selectedRunsheet?.type || TAB_TYPE_DEED)
    const [isRunsheetTab, setIsRunsheetTab] = useState(selectedRunsheet?.isTab || false)
    
    const {
        data: runsheets,
        error: runsheetsError,
        isLoading: runsheetsIsLoading,
    } = useGlobalStateQuery(store.runsheets, {
        queryKey: ['runsheets'],
        queryFn: async () => {
            try {
                return await actions.fetchRunsheets()
            } catch (e) {
                console.error('SelectRunsheetDialog#useGlobalStateQuery', e)
                toast.error('Failed to fetch runsheets')
                throw e
            }
        },
        enabled: open,
        retry: 0,
    })
    
    const handleModal = () => setOpen(prev => !prev)
    
    const onCreateRunsheetClick = () => {
        
        handleModal()
        
        // the timeout is necessary because the animation of the modal leaving bugs focusing on the first field
        setTimeout(() => {
            setCreateRunsheetDialogOpen(true)
        }, 450)
        
    }
    
    const onSaveClick = () => {
        
        // it should validate a possible missing selectedRunsheet within the function
        onSelectRunsheet?.(selectedRunsheet)
        
        handleModal()
        
    }
    
    const filteredRunsheets = useMemo(() => {
        
        if (!query.length) return runsheets
        
        return runsheets.filter(it => it.name.toLowerCase().includes(query))
        
    }, [query, runsheets])
    
    useEffect(() => {
        if (selectedRunsheet && selectedRunsheet.isTab === true) {
            // setSelectedTabType(selectedRunsheet.type)
            setOwnershipType(selectedRunsheet.type)
        }
    }, [selectedRunsheet])
    
    return (
        
        <CustomModal
            className="SelectRunsheetDialog"
            open={open}
            center
            modalHandler={() => handleModal()}>
            
            <div className="modal-body flex flex-col min-h-[calc(100vh-400px)]">
                
                <div className="flex items-center content-center mb-3 mr-10">
                    
                    {runsheetsError && (<>
                        <p>Failed to fetch runsheets.</p>
                    </>)}
                    
                    {runsheetsIsLoading && (
                        <div className="flex justify-center p-4 text-center">
                            <div className="loading loading-spinner" />
                        </div>
                    )}
                    
                    <div className="w-full">
                        {runsheets?.length > 0 ? (
                            <input
                                type="text"
                                className="input-bordered input w-full"
                                placeholder="Search"
                                value={query}
                                onChange={e => setQuery(e.target.value.toLowerCase())} />
                        ) : (
                            <h4>Select Runsheet</h4>
                        )}
                    </div>
                    
                    <div className="pl-0 ml-3 shrink-0">
                        <span
                            className="mt-1 btn btn-primary btn-md"
                            data-test-id="SelectRunsheetDialog-CreateRunsheetButton"
                            onClick={onCreateRunsheetClick}>
                            Create Runsheet
                        </span>
                    </div>
                
                </div>
                
                <div className="pt-0 pl-0 pr-0 ml-0 overflow-hidden max-h-[calc(100vh-500px)] my-4">
                    {runsheets?.length > 0 ? (
                        <div>
                            {showAssign && <div className="flex gap-2 items-center mb-4 sticky top-0 left-0 relative">
                                
                                <label>Assign as:</label>
                                
                                <div className="join">
                                    <button
                                        onClick={() => !isRunsheetTab && setOwnershipType('deed')}
                                        className={cn('btn btn-xs join-item', {
                                            'btn-primary': ownershipType === 'deed',
                                            'bg-base-100': ownershipType === 'lease',
                                            'cursor-not-allowed': isRunsheetTab,
                                        })}>
                                        Deed
                                    </button>
                                    <button
                                        onClick={() => !isRunsheetTab && setOwnershipType('lease')}
                                        className={cn('btn border btn-xs join-item', {
                                            'btn-primary': ownershipType === 'lease',
                                            'bg-base-100': ownershipType === 'deed',
                                            'cursor-not-allowed': isRunsheetTab,
                                        })}>
                                        Leasehold
                                    </button>
                                </div>
                            </div>}
                            <div className="h-[calc(100vh-500px)] overflow-auto">
                                <table className="table table-sm table-nowrap table-pin-rows card-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <a href="#!" className="text-muted sort" data-sort="name">
                                                    Runsheet Name
                                                </a>
                                            </th>
                                            <th>
                                                <a href="#!" className="text-muted sort" data-sort="gross">
                                                    Gross Acreage
                                                </a>
                                            </th>
                                            <th>
                                                <a href="#!" className="text-muted sort" data-sort="assigned">
                                                    Assigned To
                                                </a>
                                            </th>
                                            <th>
                                                <a href="#!" className="text-muted sort" data-sort="status">
                                                    Status
                                                </a>
                                            </th>
                                            {/* <th>
                                                <a href="#!" className="text-muted sort" data-sort="statusDate">
                                                    Status Date
                                                </a>
                                            </th>
                                            <th>
                                                <a href="#!" className="text-muted sort" data-sort="cert_date">
                                                    Cert. Date
                                                </a>
                                            </th>
                                            <th></th> */}
                                        </tr>
                                    </thead>
                                    <tbody className="list">
                                        {filteredRunsheets?.map(runsheet => (
                                            <React.Fragment key={runsheet.id}>
                                                <tr>
                                                    <td>
                                                        <label className="flex items-center mb-0">
                                                            <div className="pb-1 pr-3">
                                                                <input
                                                                    type="radio"
                                                                    name="runsheet"
                                                                    checked={selectedRunsheet?.id === runsheet.id}
                                                                    onChange={() => {
                                                                        setSelectedRunsheet({ ...runsheet, 
                                                                            isTab: false })
                                                                        setIsRunsheetTab(false)
                                                                        setOwnershipType('deed')
                                                                    }} />
                                                            </div>
                                                            <div className="cursor-pointer">
                                                                {runsheet.name}
                                                            </div>
                                                        </label>
                                                    </td>
                                                    <td className="align-middle">{runsheet.grossAcreage}</td>
                                                    <td className="align-middle">{`${runsheet.assignedTo 
                                                        && runsheet.assignedTo[0]
                                                        ? runsheet.assignedTo[0].firstName
                                                        : ''
                                                    }`}</td>
                                                    <td className="align-middle">{runsheet.status}</td>
                                                    {/* <td className="align-middle">{runsheet.statusDate}</td>
                                                    <td className="align-middle">{runsheet.certDate}</td> */}
                                                </tr>
                                                {showTabs && runsheet.tabs.map(tab => (
                                                    <tr key={tab.id}>
                                                        <td>
                                                            <label className="flex items-center mb-0">
                                                                <div className="pb-1 pr-3 pl-6">
                                                                    <input
                                                                        type="radio"
                                                                        name="runsheet"
                                                                        checked={selectedRunsheet?.id === tab.id}
                                                                        onChange={() => {
                                                                            setIsRunsheetTab(true)
                                                                            setSelectedRunsheet({ ...tab, isTab: true })
                                                                        }} />
                                                                
                                                                </div>
                                                                <div className="cursor-pointer flex 
                                                                    flex-row gap-2 items-center">
                                                                    {tab.type === 'lease' ? 
                                                                        <MdArticle className="text-lg" /> 
                                                                        : <MdAutoAwesomeMosaic className="text-lg" />}
                                                                    {tab.name} - ({TAB_TYPE_MAP[tab.type] || 'Unknown'})
                                                                </div>
                                                            </label>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="">
                            You don't have any runsheets yet.
                            <br />
                            Create one now to get started
                        </div>
                    )}
                </div>
            
            </div>
            
            <div className="modal-footer flex justify-end">
                <button
                    type="button"
                    className="mr-3 btn btn-primary btn-outline"
                    data-test-id="SelectRunsheetDialog-CancelButton"
                    onClick={handleModal}>
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary"
                    data-test-id="SelectRunsheetDialog-ConfirmButton"
                    disabled={!selectedRunsheet}
                    onClick={onSaveClick}>
                    {confirmButtonText}
                </button>
            </div>
        
        </CustomModal>
        
    )
    
}
